import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { StarIcon } from "@/components/ui/StarIcon";
import { useUpcomingEarnings } from "@/hooks/api";
import { cn } from "@/lib/utils";
import type { EarningsCalendarItemOut } from "@/api/client";

export function CalendarSidebar({
  selectedId,
  onSelect,
}: {
  selectedId: number | null;
  onSelect: (item: EarningsCalendarItemOut) => void;
}) {
  const [query, setQuery] = useState("");
  const { data, isLoading } = useUpcomingEarnings(100);

  const list = useMemo(() => {
    const rows = data ?? [];
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter(
      (r) =>
        r.company_name.toLowerCase().includes(q) ||
        (r.primary_symbol ?? "").toLowerCase().includes(q),
    );
  }, [data, query]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b border-border space-y-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search company or ticker..."
            className="pl-7 h-7 text-[12px]"
          />
        </div>
        <div className="flex items-center text-[10px] text-muted-foreground font-mono">
          <span>UPCOMING EARNINGS</span>
          <span className="ml-auto">{isLoading ? "…" : list.length}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">Loading…</div>
        )}
        {!isLoading && list.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
            No upcoming earnings. Run the earnings-calendar deployment to seed data.
          </div>
        )}
        {list.map((item) => (
          <CalendarRow
            key={item.id}
            item={item}
            selected={selectedId === item.id}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function CalendarRow({
  item,
  selected,
  onSelect,
}: {
  item: EarningsCalendarItemOut;
  selected: boolean;
  onSelect: (item: EarningsCalendarItemOut) => void;
}) {
  return (
    <button
      onClick={() => onSelect(item)}
      className={cn(
        "w-full text-left px-2 py-1.5 border-b border-border/60 hover:bg-accent/60 transition-colors block min-h-[44px] md:min-h-0",
        selected && "bg-accent",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-1 min-w-0">
          {item.starred && <StarIcon className="h-2.5 w-2.5 self-center" />}
          <div className="font-mono font-semibold text-[12px] whitespace-nowrap">
            {item.primary_symbol ?? "—"}
          </div>
        </div>
        <div className="font-mono text-[11px] tabular-nums whitespace-nowrap text-muted-foreground">
          {formatShortDate(item.announcement_date)}
        </div>
      </div>
      <div className="flex items-baseline justify-between gap-2 mt-0.5 min-w-0">
        <div className="text-[10px] text-muted-foreground truncate min-w-0">{item.company_name}</div>
        <div className="text-[10px] font-mono tabular-nums text-muted-foreground shrink-0">
          {item.fiscal_period ?? ""}
        </div>
      </div>
    </button>
  );
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
